import { Card, CardContent, Typography, Box, Stack, Chip } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import type { UserBusinessDetailsDto } from '../../../api/userManagementApi';

interface UserBusinessDetailsCardProps {
  businessDetails: UserBusinessDetailsDto;
}

export function UserBusinessDetailsCard({
  businessDetails,
}: UserBusinessDetailsCardProps) {
  // If admin skipped business details (isBookingWork = false), don't render
  if (!businessDetails.isBookingWork) {
    return null;
  }

  const getBusinessType = () => {
    if (businessDetails.soleTrader) return 'Sole Trader';
    if (businessDetails.limitedCompany) return 'Limited Company';
    return 'Not specified';
  };

  return (
    <Card variant="outlined" data-testid="user-business-details-card">
      <CardContent>
        <Stack spacing={2}>
          {/* Card Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon fontSize="small" color="action" />
            <Typography variant="h6" component="h3">
              Business Details
            </Typography>
          </Box>

          {/* Business Details */}
          <Stack spacing={1.5}>
            {/* Business Type */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                Business Type
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {getBusinessType()}
              </Typography>
            </Box>

            {/* Trading Name (if present) */}
            {businessDetails.businessName && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Trading Name
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {businessDetails.businessName}
                </Typography>
              </Box>
            )}

            {/* Sole Trader Fields */}
            {businessDetails.soleTrader && (
              <>
                {businessDetails.soleTrader.hmrcFullName && (
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      HMRC Full Name
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {businessDetails.soleTrader.hmrcFullName}
                    </Typography>
                  </Box>
                )}
                {businessDetails.soleTrader.utr && (
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      UTR
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {businessDetails.soleTrader.utr}
                    </Typography>
                  </Box>
                )}
                {businessDetails.soleTrader.nationalInsuranceNumber && (
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      National Insurance Number
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {businessDetails.soleTrader.nationalInsuranceNumber}
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {/* Limited Company Fields */}
            {businessDetails.limitedCompany && (
              <>
                {businessDetails.limitedCompany.companyName && (
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Registered Company Name
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {businessDetails.limitedCompany.companyName}
                    </Typography>
                  </Box>
                )}
                {businessDetails.limitedCompany.utr && (
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Company UTR
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {businessDetails.limitedCompany.utr}
                    </Typography>
                  </Box>
                )}
                {businessDetails.limitedCompany.companyRegistrationNumber && (
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Company Registration Number
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {businessDetails.limitedCompany.companyRegistrationNumber}
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {/* Completion Status */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Status
              </Typography>
              <Chip
                label={businessDetails.isComplete ? 'Complete' : 'Incomplete'}
                size="small"
                color={businessDetails.isComplete ? 'success' : 'warning'}
              />
            </Box>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
